import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  Paper,
  FormControl,
  InputLabel
} from '@mui/material';
import CustomTreeView from '../CustomTreeView/CustomTreeView';
import { getPages, getPermisisons, getUsersExceptLogin, postRequest } from '../../api/axios';
import { getPermissions, getUsername } from '../../context/AuthFunc';
import { PERMISSION_ENDPOINT } from '../../constants/const';
import Error from '../Error/Error';
import Success from '../Success/Success';
import { useTranslation } from 'react-i18next';

const AccessRights = () => {
  const { t } = useTranslation('global');

  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [errMsg, setErrMsg] = useState('');
  const [succ, setSucc] = useState('');

  const extractPageIdsFromToken = (permissions) => {
  return permissions.map(permission => parseInt(permission.split('_')[0]));
};

const buildTreeData = (pages) => {
  // Group pages into logical categories
  const categories = {
    home: { id: 'home', label: t("navbar.home"), children: [] },
    tickets: { id: 'tickets', label: t("navbar.tickets"), children: [] },
    reports: {id: 'reports', label: t("navbar.reports"), children: []},
    admin: { id: 'admin', label: t("navbar.admin"), children: [] },
    sales: { id: 'sales', label: t("navbar.sales"), children: [] },
    fiscalization: {id: 'fiscalization', label: t("navbar.fiscalization"), children: []},
    canteen: { id: 'canteen', label: t("navbar.canteen"), children: [] },
    settings: { id: 'settings', label: t("navbar.Settings"), children: [] },
    navbar: {id: 'navbar', label: t("navbar.hik"), children: []}
  };

  // Map pages to categories
  pages.forEach(page => {
    if (page.path === '/home' || page.path === '/ticket-info' || page.path === '/ticket/edit' || page.path === '/search-users') {
      categories.home.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/active', '/passed', '/nonactive', '/sold'].includes(page.path)) {
      categories.tickets.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/category', '/kind', '/type', '/dailyrep', '/log_card'].includes(page.path)) {
      categories.reports.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/template-groups','/template-group-events','/ticket-design','/new-reader', '/readers', '/readers/edit', '/new-user', '/access', '/readers', '/logs'].includes(page.path)) {
      categories.admin.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/new-kind','/new-category','/new-ticket', '/new-item', '/price-list/edit', '/price-list/delete', '/price-list', '/monitoring', '/purchasing', '/payment_method'].includes(page.path)) {
      categories.sales.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/settings', '/update-database'].includes(page.path)) {
      categories.settings.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/refund', '/fiscalization-reports'].includes(page.path)) {
      categories.fiscalization.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if (['/new-employee', '/new-organization-unit', '/new-article', '/new-unit-of-measure', '/new-ingredient'].includes(page.path)) {
      categories.canteen.children.push({ id: page.id, label: t(`access.${page.id}`) });
    } else if ((['/hik']).includes(page.path)){
      categories.navbar.children.push({ id: page.id, label: t(`access.${page.id}`)});
    }
  });

  return Object.values(categories);
};

const getCheckedItems = (permissions) => {
  return permissions.map(p => p.page_id.toString());
};


  useEffect(() => {
    fetchUsers();
    setUserPermissions(getPermissions());
  }, []);

  useEffect(() => {
    fetchPages();
    if (selectedUser) {
      fetchUserPermissions(selectedUser.id);
    }
  }, [selectedUser]);

  const fetchUsers = async () => {
    const username = getUsername();
    const response = await getUsersExceptLogin(username);
    setUsers(response);
  };

  const fetchPages = async () => {
    const response = await getPages();
    const allowedPageIds = extractPageIdsFromToken(userPermissions);
    const filteredPages = response.filter(page => allowedPageIds.includes(parseInt(page.id)));
    setPages(filteredPages);
  };

  const fetchUserPermissions = async (user_id) => {
    const response = await getPermisisons(user_id);
    setPermissions(response);
  };

  const handlePermissionChange = async (pageId, permission, isChecked) => {
    const action = isChecked ? 'add' : 'remove';

    const formData = new URLSearchParams();
    formData.append('user_id', selectedUser.id);
    formData.append('page_id', pageId);
    formData.append('action', action);

    const response = await postRequest(PERMISSION_ENDPOINT, formData);

    if (response.success) {
      fetchUserPermissions(selectedUser.id);
      setSucc(t("authorization.success"));
      setErrMsg('');
    } else {
      setErrMsg(t("authorization.unsuccessful"));
    }
  };

  const handleUserChange = (e) => {
    const selectedUserId = e.target.value;
    const user = users.find(user => user.id === selectedUserId);
    if (user) {
      setSelectedUser(user);
    } else {
      console.error(t("authorization.selectedUserNotFound"));
    }
  };

  const treeData = buildTreeData(pages);
  const checkedItems = getCheckedItems(permissions);

  const handleTreeCheck = async (nodeId, isChecked) => {
    // Handle parent nodes
    const parentCategory = treeData.find(cat => cat.id === nodeId);
    if (parentCategory) {
      // Check/uncheck all children
      for (const child of parentCategory.children) {
        await handlePermissionChange(child.id, 'view', isChecked);
      }
      return;
    }
    
    // Handle leaf nodes
    await handlePermissionChange(nodeId, 'view', isChecked);
    
    // Get fresh permissions after update
    const updatedPermissions = await getPermisisons(selectedUser.id);
    
    // Find parent of this node and check if all siblings are checked
    for (const category of treeData) {
      const childNode = category.children.find(child => child.id === nodeId);
      if (childNode) {
        const allChildrenChecked = category.children.every(child => 
          updatedPermissions.some(p => p.page_id.toString() === child.id)
        );
        if (allChildrenChecked) {
          await handlePermissionChange(category.id, 'view', true);
        }
        break;
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom sx={{ pt: 4, pl: 1 }}>
        {t("authorization.managingUserAccessLevel")}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      </Box>
      <FormControl fullWidth sx={{ marginBottom: 3 }}>
        <InputLabel>{t("authorization.selectUser")}</InputLabel>
        <Select
          value={selectedUser ? selectedUser.id : ''}
          onChange={handleUserChange}
        >
          <MenuItem value="">
            <em>{t("authorization.selectUser")}</em>
          </MenuItem>
          {users.map(user => (
            <MenuItem key={user.id} value={user.id}>
              {user.username}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedUser && (
        <Box>
          <Typography variant="h6" sx={{ pt: 4, pl: 1, mb: 2}}>
            {t("authorization.userAccessLevel")} {selectedUser.username}
          </Typography>
          <Paper elevation={3} sx={{ p: 2 }}>
            <CustomTreeView
              items={treeData}
              checkedItems={checkedItems}
              onCheck={handleTreeCheck}
            />
          </Paper>
        </Box>
      )}
      {errMsg && <Error message={errMsg} onClose={() => setErrMsg('')} />}
      {succ && <Success message={succ} onClose={() => setSucc('')} />}
    </Box>
  );
};

export default AccessRights;


{/*                <td>
                    <input
                      type="checkbox"
                      checked={permissions.some(p => p.page_id === page.id && p.permission === 'view')}
                      onChange={(e) => handlePermissionChange(page.id, 'view', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={permissions.some(p => p.page_id === page.id && p.permission === 'edit')}
                      onChange={(e) => handlePermissionChange(page.id, 'edit', e.target.checked)}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={permissions.some(p => p.page_id === page.id && p.permission === 'create')}
                      onChange={(e) => handlePermissionChange(page.id, 'create', e.target.checked)}
                    />
                  </td> */}
